/**
 * @module engine/formulation
 * @summary The recipes a drawing's lines run, and what that makes each stream.
 * @description
 *   A compounder works from a formulation and a line rate. The recipe says
 *   what the compound is — in percent, because that is what a formulation IS:
 *   it outlives a change of rate, and the same recipe runs at 800 kg/h on one
 *   line and 1500 on another. The rate says how fast the line runs. Everything
 *   else follows:
 *
 *       material kg/h = line total × its % ÷ 100
 *       feeder kg/h   = that, × the share of it dosed at this feeder
 *
 *   Nothing is typed twice. A feeder is told which LINE it belongs to and
 *   which of that line's materials it doses — never how many kilograms, because
 *   the recipe already settles that, and a second number would be free to
 *   disagree with the first.
 *
 *   Where the finished good begins: the compound is MADE in the extruder or
 *   kneader, so its outlet is where ingredients stop and product starts, and
 *   everything from there to the packing area carries the finished good. A
 *   shape's own `fgStart` overrides that in either direction.
 *
 *   Drawings made before any of this behave exactly as they did: a feeder that
 *   belongs to no formulation keeps its own `rate`, and may name any material.
 *
 *   // INVARIANT: nothing here writes to the document. Like `balance`, it is
 *   called from both views' draw paths, and a reader that edits is a disaster.
 */
import { EP_INDEX, RESIN_CATEGORIES } from '../data/epMaterialIndex';
import { S } from './state';
import type { FeedItem, Formulation, Shape } from '../types';

/**
 * What a stream is made of: component key → kg/h.
 *
 * A key is either an EP part number ("RE01001") or, past the FG point, the
 * finished good (see `fgKey`). Keeping both in one map is what lets the same
 * merge-and-split arithmetic run either side of the compounding step.
 */
export type Mix = Map<string, number>;

/** The component key for the finished good made at `shapeId`. */
export const fgKey = (shapeId: string): string => `fg:${shapeId}`;

/** The shape id behind an FG component key, or undefined for a raw material. */
export const fgShapeOf = (key: string): string | undefined =>
  key.startsWith('fg:') ? key.slice(3) : undefined;

// ─── Where the finished good begins ──────────────────────────────────────

/**
 * The machines that MAKE the compound, and so end the recipe.
 *
 * Past one of these the breakdown stops being useful and the name of the
 * product starts being useful: what leaves a kneader is the compound, whatever
 * it is pelletized and dried by afterwards.
 */
export const FG_START_TYPES: ReadonlySet<string> = new Set(['tse', 'sse', 'coknead']);

/** True when the finished good begins at this item. */
export function isFgStart(s: Shape): boolean {
  return s.fgStart ?? FG_START_TYPES.has(s.type);
}

// ─── The recipes on this drawing ─────────────────────────────────────────

/** Every formulation on the drawing. */
export function allFormulations(): Formulation[] {
  return S.formulations;
}

/** One by id. */
export function formulationById(id: string | undefined): Formulation | undefined {
  return id ? S.formulations.find((f) => f.id === id) : undefined;
}

/** The line a shape belongs to, if it has been put on one. */
export function formulationOf(s: Shape | undefined): Formulation | undefined {
  return formulationById(s?.formulationId);
}

/** How the line is named in a dropdown, falling back to something stable. */
export function formulationName(f: Formulation): string {
  return f.fg.trim() || 'Unnamed line';
}

const positive = (v: number | undefined): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;

/** The recipe percentage of one material, 0 when the line does not contain it. */
export function pctOf(f: Formulation, part: string): number {
  let pct = 0;
  for (const l of f.lines) if (l.part === part) pct += positive(l.pct);
  return pct;
}

/** kg/h of one material the whole LINE needs. */
export function materialKgh(f: Formulation, part: string): number {
  return (positive(f.totalKgh) * pctOf(f, part)) / 100;
}

/** The recipe percentages as typed. Should be 100; it is the author's job. */
export function pctTotal(f: Formulation): number {
  let t = 0;
  for (const l of f.lines) t += positive(l.pct);
  return t;
}

/** The feed rows of a shape, ignoring any that name no material. */
export function feedOf(s: Shape | undefined): FeedItem[] {
  return (s?.feed ?? []).filter((f) => f && f.part);
}

/** The share of a material dosed at one feeder, in % — all of it by default. */
export function shareOf(row: FeedItem): number {
  return row.share === undefined ? 100 : Math.max(0, row.share);
}

/**
 * How much this item charges in, and of what.
 *
 * Two worlds, and which one applies is decided by whether the item has been
 * put on a line:
 *
 *   • **On a line** — the recipe rules. Each row contributes its material's
 *     line requirement times the share dosed here. Nothing on the shape says
 *     how many kilograms, so nothing on the shape can contradict the recipe.
 *   • **On no line** — the old behaviour, unchanged. Itemised `kgh` if any row
 *     carries one, a single row taking the item's `rate`, or a bare `rate` with
 *     no material named at all. This is what keeps a drawing made before
 *     recipes existed balancing exactly as it did.
 */
export function addedAt(s: Shape): { total: number; mix: Mix } {
  const rows = feedOf(s);
  const mix: Mix = new Map();
  const f = formulationOf(s);

  if (f) {
    let total = 0;
    for (const r of rows) {
      const kgh = (materialKgh(f, r.part) * shareOf(r)) / 100;
      if (!kgh) continue;
      mix.set(r.part, (mix.get(r.part) ?? 0) + kgh);
      total += kgh;
    }
    return { total, mix };
  }

  const rate = positive(s.rate);
  if (!rows.length) return { total: rate, mix };

  if (rows.length === 1 && positive(rows[0].kgh) === 0) {
    if (rate > 0) mix.set(rows[0].part, rate);
    return { total: rate, mix };
  }

  let total = 0;
  for (const r of rows) {
    const kgh = positive(r.kgh);
    if (!kgh) continue;
    mix.set(r.part, (mix.get(r.part) ?? 0) + kgh);
    total += kgh;
  }
  return { total, mix };
}

// ─── Mixture arithmetic ──────────────────────────────────────────────────

/** Add `src` into `dst`, in place. */
export function addMix(dst: Mix, src: Mix): void {
  for (const [k, v] of src) if (v) dst.set(k, (dst.get(k) ?? 0) + v);
}

/** `src` scaled by `f`, as a new mixture. Used to split a stream. */
export function scaleMix(src: Mix, f: number): Mix {
  const out: Mix = new Map();
  if (!Number.isFinite(f) || f <= 0) return out;
  for (const [k, v] of src) if (v) out.set(k, v * f);
  return out;
}

/** Total kg/h across every component. */
export function mixTotal(m: Mix): number {
  let t = 0;
  for (const v of m.values()) t += v;
  return t;
}

// ─── Naming ──────────────────────────────────────────────────────────────

/** What a raw material is called, falling back to its part number. */
export function materialName(part: string): string {
  return EP_INDEX[part]?.name ?? part;
}

/** Its category, or '' when the part is not one of ours. */
export function materialCategory(part: string): string {
  return EP_INDEX[part]?.category ?? '';
}

/** True when this part is a base polymer, which is what phr is per hundred of. */
export function isResin(part: string): boolean {
  return RESIN_CATEGORIES.has(materialCategory(part));
}

/**
 * A material's name shortened enough to sit on a pipe without covering it.
 *
 * The workbook writes descriptions for a table ("POLY VINYL CHLORIDE - PVC
 * 70K"), and a flow sheet has room for a few characters. Where a description
 * carries a trailing grade after a dash, that grade is the part an engineer
 * reads, so it is what survives.
 */
export function shortMaterialName(part: string): string {
  const full = materialName(part);
  // A trailing code after a slash ("CALCIUM CARBONATE FINE / 1JLTR") identifies
  // the drum it arrives in, not the material, and is dead weight on a pipe.
  const noCode = full.split(' / ')[0].trim();
  const dash = noCode.lastIndexOf(' - ');
  const tail = dash === -1 ? noCode : noCode.slice(dash + 3).trim();
  const pick = tail.length >= 4 && tail.length <= 24 ? tail : noCode;
  return pick.length <= 24 ? pick : pick.slice(0, 23) + '…';
}

// ─── Colour, for the flow ────────────────────────────────────────────────

/**
 * Slug colours, in the order materials are assigned them.
 *
 * Picked to stay apart under the flow's own lighting, which multiplies them up
 * to read as lit from inside the pipe — so these are chosen for HUE separation
 * rather than brightness, and none of them is the cyan that already means air.
 * Assignment is by position in the recipe, so a colour is stable for as long as
 * the recipe is and the legend always matches what is moving.
 */
export const MATERIAL_COLOURS: readonly string[] = [
  '#e8873a', // amber — the original material colour, so a one-material line looks unchanged
  '#e5e2d8', // bone
  '#8fbf5a', // olive
  '#c2607a', // rose
  '#9b7fd4', // violet
  '#4aa89a', // teal
  '#d4b13f', // ochre
  '#7f96c4', // slate blue
  '#c9705a', // terracotta
  '#6fae7c', // sage
];

/** The finished good's own colour — deliberately unlike any ingredient. */
export const FG_COLOUR = '#2f6fd0';

/** Hex to the 0..1 triple the renderer mixes into a pipe. */
export function hexTriple(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/**
 * The colour a material flows in on a given line.
 *
 * By position in the recipe rather than by part number, so the legend beside
 * the flow reads top to bottom in the same order as the formulation sheet.
 */
export function colourForPart(f: Formulation | undefined, part: string): string {
  const i = f ? f.lines.findIndex((l) => l.part === part) : -1;
  if (i >= 0) return MATERIAL_COLOURS[i % MATERIAL_COLOURS.length];
  // Not on a line — fall back to something stable for this part number, so an
  // unbound drawing still gets distinguishable streams.
  let h = 0;
  for (let k = 0; k < part.length; k++) h = (h * 31 + part.charCodeAt(k)) >>> 0;
  return MATERIAL_COLOURS[h % MATERIAL_COLOURS.length];
}

// ─── The sheet ───────────────────────────────────────────────────────────

/** Where one material is actually dosed, and how much of it. */
export interface ChargePoint {
  shapeId: string;
  label: string;
  /** % of this material's line requirement dosed here. */
  share: number;
  kgh: number;
}

/** One material on the formulation sheet, with everything derived. */
export interface RecipeLine {
  part: string;
  name: string;
  category: string;
  /** As typed on the recipe. */
  pct: number;
  /** What the line needs: total × pct. */
  kgh: number;
  /**
   * Parts per hundred resin — the unit a cable compounder works in.
   * `undefined` when the recipe contains no resin at all, because then there is
   * nothing for it to be "per hundred" OF and a number would be invented.
   */
  phr?: number;
  /** The feeders dosing it. */
  from: ChargePoint[];
  /** Their shares added up. 100 is right; anything else is worth flagging. */
  shareTotal: number;
  /** The colour this material flows in. */
  colour: string;
}

/** A whole line: its recipe, where it is charged, and what it makes. */
export interface Recipe {
  formulation: Formulation;
  lines: RecipeLine[];
  /** Recipe percentages as typed — 100 when the sheet is complete. */
  pctTotal: number;
  /** kg/h of resin, the basis for phr. */
  resinKgh: number;
  /** Materials on the recipe that no feeder on the drawing doses. */
  unfed: string[];
  /** Materials whose feeder shares do not add up to 100%. */
  misfed: string[];
}

/** Build the sheet for one line, from the drawing's charge points. */
export function buildRecipe(f: Formulation, shapes: Shape[]): Recipe {
  const total = positive(f.totalKgh);

  const charge = new Map<string, ChargePoint[]>();
  for (const s of shapes) {
    if (s.formulationId !== f.id) continue;
    for (const r of feedOf(s)) {
      const share = shareOf(r);
      const list = charge.get(r.part) ?? [];
      list.push({
        shapeId: s.id,
        label: s.label || s.tag || s.type,
        share,
        kgh: (materialKgh(f, r.part) * share) / 100,
      });
      charge.set(r.part, list);
    }
  }

  let resinKgh = 0;
  for (const l of f.lines) if (isResin(l.part)) resinKgh += (total * positive(l.pct)) / 100;

  const unfed: string[] = [];
  const misfed: string[] = [];
  const lines: RecipeLine[] = f.lines.map((l, i) => {
    const from = (charge.get(l.part) ?? []).sort((a, b) => b.kgh - a.kgh);
    const shareTotal = from.reduce((t, c) => t + c.share, 0);
    if (!from.length) unfed.push(l.part);
    else if (Math.abs(shareTotal - 100) > 0.01) misfed.push(l.part);
    const kgh = (total * positive(l.pct)) / 100;
    return {
      part: l.part,
      name: materialName(l.part),
      category: materialCategory(l.part),
      pct: positive(l.pct),
      kgh,
      phr: resinKgh > 0 ? (kgh / resinKgh) * 100 : undefined,
      from,
      shareTotal,
      colour: MATERIAL_COLOURS[i % MATERIAL_COLOURS.length],
    };
  });

  return { formulation: f, lines, pctTotal: pctTotal(f), resinKgh, unfed, misfed };
}

/** Every line's sheet. */
export function buildRecipes(shapes: Shape[]): Recipe[] {
  return S.formulations.map((f) => buildRecipe(f, shapes));
}

/** How a finished good is written on a line when it has not been named yet. */
export function fgDisplayName(f: Formulation | undefined, fallback: string): string {
  return f?.fg.trim() || fallback;
}

/** A dose as a batch sheet writes it. */
export function formatPhr(phr: number | undefined): string {
  if (phr === undefined || !Number.isFinite(phr)) return '—';
  if (phr >= 100) return phr.toFixed(1);
  if (phr >= 1) return phr.toFixed(2);
  return phr.toFixed(3);
}

/** A percentage as a batch sheet writes it. */
export function formatPct(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  return pct >= 10 ? pct.toFixed(1) + '%' : pct.toFixed(2) + '%';
}
